import React, { useState, useEffect, useRef } from 'react';
import { Transition } from "@headlessui/react";
import { useSelector } from "react-redux";
import EditIcon from "../assets/ic_pencil.svg";
import { verifyAndSaveDomain } from "../redux/api/organisationAPI";
import { ClipboardCopyIcon, CheckIcon } from "@heroicons/react/outline";
import Card from "../components/shared/Card";
import Button from "../components/shared/Button";
import TextInput from "../components/shared/TextInput";
import { useIsAdmin } from "../hooks/useIsAdmin";

const SettingsPage = () => {
    const isAdmin = useIsAdmin();
    const [domain, setDomain] = useState('');
    const [isDomainValid, setIsDomainValid] = useState(false);
    const [isVerifying, setIsVerifying] = useState(false);
    const [isVerified, setIsVerified] = useState(false);
    const [isSaved, setIsSaved] = useState(false);
    const [isEditable, setIsEditable] = useState(true);
    const [highlightInput, setHighlightInput] = useState(false);
    const [verificationFailed, setVerificationFailed] = useState(false);
    const [copiedOrgId, setCopiedOrgId] = useState(false);
    const [copiedApiKey, setCopiedApiKey] = useState(false);
    const [copiedVerification, setCopiedVerification] = useState(false);
    const inputRef = useRef(null);

    const currentOrganisation = useSelector((state) => state.organisation?.currentOrganisation);

    useEffect(() => {
        if (currentOrganisation?.settings?.shop?.isVerified) {
            const customDomain = currentOrganisation?.settings?.shop?.customShopDomain;
            setDomain(`${customDomain}`.replace("https://", ""));
            setIsVerified(true);
            setIsSaved(true);
            setIsEditable(false);
        } else {
            setDomain('');
            setIsVerified(false);
            setIsSaved(false);
            setIsEditable(true);
        }
    }, [currentOrganisation]);


    const triggerHighlight = (color) => {
        setHighlightInput(color);
        setTimeout(() => setHighlightInput(false), 1000);
    };

    const handleVerificationAndSave = async () => {
        setIsVerifying(true);

        try {
            const result = await verifyAndSaveDomain(domain, currentOrganisation?._id);
            setIsVerifying(false);
            if (result && result.data) {
                setIsVerified(true);
                setIsSaved(true);
                setIsEditable(false);
                setVerificationFailed(false);
                setIsDomainValid(false);
            } else {
                setIsSaved(false);
                setIsEditable(true);
                setVerificationFailed(true);
                triggerHighlight('red');
                setTimeout(() => {
                    setVerificationFailed(false);
                }, 3000);
            }
        } catch (e) {
            setIsVerifying(false);
            setIsVerified(false);
            setIsSaved(false);
            setVerificationFailed(true);
            triggerHighlight('red');
            setTimeout(() => {
                setVerificationFailed(false);
            }, 3000);

            console.error(e);
        }
    };

    const handleDomainChange = (e) => {
        const value = e.target.value;
        const domainIsValid = validateDomain(value);
        setDomain(value);
        setIsDomainValid(domainIsValid);

        if (domainIsValid) {
            setIsEditable(true);
            setVerificationFailed(false);
            if (!isVerified) {
                setIsSaved(false);
            }
        } else {
            setIsVerified(false);
            setIsSaved(false);
        }
    };

    const handleEditClick = () => {
        setIsDomainValid(true);
        setIsVerified(false);
        setIsSaved(false);
        setIsEditable(true);
        triggerHighlight('blue');
        inputRef.current?.focus();
    };

    const validateDomain = (domain) => {
        return /^[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*(\.[a-zA-Z]{2,})$/.test(domain);
    };

    const copyToClipboard = (text, setCopiedState) => {
        navigator.clipboard.writeText(text).then(() => {
            setCopiedState(true);
            setTimeout(() => setCopiedState(false), 2000);
        }).catch((error) => {
            console.error('Failed to copy text: ', error);
        });
    };

    const verificationString = currentOrganisation?.settings?.shop?.metaData?.domainVerificationString;
    const verificationText = `loyalbase-site-verification=${verificationString}`;
    const accessKey = currentOrganisation?.accessKeys?.[0];

    return (
        <div className="bg-canvas py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto space-y-6" data-help-title="Settings" data-help="Holds the technical details for connecting LoyalBase to your own systems: your Organisation ID, your API key, and the optional custom domain for your loyalty store.">
                {/* Organisation ID Section */}
                <Card variant="default">
                    <h3 className="text-title-md text-ink mb-4">Organisation ID</h3>
                    <p className="text-body-md text-muted mb-3">
                        Your Organisation ID
                    </p>
                    <div className="flex items-center gap-3">
                        <div className="flex-1">
                            <TextInput
                                type="text"
                                readOnly
                                value={currentOrganisation?._id || ''}
                                className="font-mono"
                                data-help-title="Organisation ID"
                                data-help="Shows your organisation's unique ID in LoyalBase. Use it to identify your organisation in API requests, integration configs, and when sharing configuration details with the LoyalBase team."
                            />
                        </div>
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => copyToClipboard(currentOrganisation?._id, setCopiedOrgId)}
                            leadingIcon={copiedOrgId ? <CheckIcon className="h-4 w-4 text-signature-forest" /> : <ClipboardCopyIcon className="h-4 w-4" />}
                            data-help-title="Copy Organisation ID"
                            data-help="Copies the full Organisation ID to your clipboard."
                        >
                            {copiedOrgId ? "Copied" : "Copy"}
                        </Button>
                    </div>
                    <p className="mt-3 text-body-md text-muted">
                        Use this ID when making API requests to identify your organisation.
                    </p>
                </Card>

                {/* API Key Instructions */}
                <Card variant="default">
                    <h3 className="text-title-md text-ink mb-3">API Key Instructions</h3>
                    <div className="text-body-md text-body space-y-3">
                        <p>Your API Key is a unique identifier that allows you to authenticate and securely access our API. Follow these steps to use your API Key:</p>
                        <ol className="list-decimal list-inside space-y-2">
                            <li>Copy the API Key provided below.</li>
                            <li>Include it in the Authorization header of your API requests as a Bearer token.</li>
                            <li>For example, when making a request, set the Authorization header like this:
                                <pre className="bg-surface-soft border border-hairline p-3 mt-2 rounded-sm text-ink"><code>Authorization: Bearer YOUR_API_KEY</code></pre>
                            </li>
                            <li>Keep your API Key secure and do not share it publicly.</li>
                        </ol>
                    </div>
                </Card>

                {/* API Key */}
                <Card variant="default">
                    <h3 className="text-title-md text-ink mb-4">API Key</h3>
                    <div className="flex items-center gap-3">
                        <div className="flex-1">
                            <TextInput
                                type="text"
                                readOnly
                                value={accessKey || ''}
                                className="font-mono"
                                data-help-title="API key"
                                data-help="Shows your organisation's secret API key. It is sent as a Bearer token in the Authorization header to authenticate API calls. Treat it like a password: anyone with it can act as your organisation."
                            />
                        </div>
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => copyToClipboard(accessKey, setCopiedApiKey)}
                            leadingIcon={copiedApiKey ? <CheckIcon className="h-4 w-4 text-signature-forest" /> : <ClipboardCopyIcon className="h-4 w-4" />}
                            data-help-title="Copy API key"
                            data-help="Copies the secret API key to your clipboard so you can move it to your application's secret store without retyping. Never commit it to source control or share it publicly."
                        >
                            {copiedApiKey ? "Copied" : "Copy"}
                        </Button>
                    </div>
                </Card>

                {/* Shop Domain */}
                <Card variant="default">
                    <h3 className="text-title-md text-ink mb-4">Shop Domain Instructions</h3>
                    <label htmlFor="subdomain" className="block text-body-md font-medium text-ink mb-2">
                        Enter Domain
                    </label>
                    <div className="flex items-stretch relative group">
                        <span className="bg-surface-soft text-muted px-3 flex items-center border border-r-0 border-hairline rounded-l-sm text-body-md">
                            https://
                        </span>
                        <input
                            id="subdomain"
                            name="subdomain"
                            type="text"
                            value={domain}
                            onChange={handleDomainChange}
                            ref={inputRef}
                            className={`appearance-none block w-full px-3 h-11 border border-hairline rounded-r-sm bg-canvas text-ink text-body-md placeholder:text-muted focus:outline-none focus:border-info-border transition-all ${
                                highlightInput === 'red' || verificationFailed ? 'border-signature-coral' : ''
                            }`}
                            placeholder="e.g., shop.example.com"
                            readOnly={!isEditable || !isAdmin}
                            data-help-title="Shop domain"
                            data-help="Field for the custom domain where your loyalty store will be served, for example shop.example.com. Validates the format as you type and becomes read-only once the domain is verified and saved. Only admins can change it."
                        />
                        {isAdmin && isSaved && !isEditable && (
                            <button
                                type="button"
                                onClick={handleEditClick}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted hover:text-ink"
                                data-help-title="Edit domain"
                                data-help="Unlocks the verified shop domain field so you can enter a different domain. Changing the domain restarts the verification process. Admin only."
                            >
                                <img className='h-5 w-5' src={EditIcon} alt="Edit" />
                            </button>
                        )}
                        {(isVerified || (isDomainValid && !isSaved && !verificationFailed)) && (
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-5 w-5 text-signature-forest absolute right-3 top-1/2 -translate-y-1/2"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                            >
                                <path
                                    fillRule="evenodd"
                                    d="M16.707 5.293a1 1 0 00-1.414 0L8 12.586 4.707 9.293a1 1 0 00-1.414 1.414l4 4a 1 1 0 001.414 0l8-8a 1 1 0 000-1.414z"
                                    clipRule="evenodd"
                                />
                            </svg>
                        )}
                    </div>

                    <Transition
                        show={isDomainValid}
                        enter="transition-opacity duration-500"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="transition-opacity duration-500"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="mt-4 text-body-md text-body">
                            <ol className="list-decimal list-inside space-y-2">
                                <li>Log in to your domain registrar's website.</li>
                                <li>Navigate to the <strong>DNS settings</strong> or domain management section.</li>
                                <li>Create a new <strong>A record</strong>:</li>
                                <ol className="list-decimal list-inside ml-5 space-y-2">
                                    <li>In the <strong>Host</strong> field, enter the subdomain you want to use (e.g., <code><strong>shop</strong>.xyz.com</code>). Leave it blank if you're setting this for the root domain (e.g., <code>xyz.com</code>).</li>
                                    <li>In the <strong>Points to</strong> field, enter <code>18.156.180.220</code>.</li>
                                    <li>Save the record and allow time for the DNS changes to propagate.</li>
                                </ol>

                                {!isSaved && (
                                    <li><strong>Verification:</strong> To verify domain ownership, follow these steps:
                                        <ul className="list-disc list-inside ml-4 space-y-1">
                                            <li>Create a new <strong>TXT</strong> record in your DNS settings.</li>
                                            <li>In the <strong>Host</strong> or <strong>Name</strong> field, enter <code>@</code> for the root domain or the specific subdomain.</li>
                                            <li>In the <strong>Value</strong> field, enter the following verification string:</li>
                                            <div className="mt-2 p-4 bg-surface-soft border border-hairline rounded-sm">
                                                <div className="flex items-center gap-3">
                                                    <div className="flex-1">
                                                        <TextInput
                                                            type="text"
                                                            readOnly
                                                            value={verificationText}
                                                            className="font-mono"
                                                            data-help-title="DNS verification string"
                                                            data-help="Shows the verification string to add as a TXT record in your DNS settings. It proves to LoyalBase that you own the domain. Copy it directly from here to avoid typos."
                                                        />
                                                    </div>
                                                    <Button
                                                        variant="secondary"
                                                        size="sm"
                                                        onClick={() => copyToClipboard(verificationText, setCopiedVerification)}
                                                        leadingIcon={copiedVerification ? <CheckIcon className="h-4 w-4 text-signature-forest" /> : <ClipboardCopyIcon className="h-4 w-4" />}
                                                        data-help-title="Copy verification string"
                                                        data-help="Copies the full TXT-record verification string to your clipboard so you can paste it into your DNS settings without typos."
                                                    >
                                                        {copiedVerification ? "Copied" : "Copy"}
                                                    </Button>
                                                </div>
                                            </div>
                                            <li>Save the record and wait for the DNS changes to propagate.</li>
                                        </ul>
                                    </li>
                                )}
                            </ol>
                        </div>
                    </Transition>

                    {isSaved && (
                        <div className="mt-4 p-4 rounded-sm bg-signature-mint border-l-4 border-signature-forest" data-help-title="Domain verified" data-help="Confirms the shop domain has been verified and saved successfully. Your loyalty store is available at this address.">
                            <h4 className="text-signature-forest font-medium">Domain Verified</h4>
                            <p className="text-body text-body-md">Domain <strong>{domain}</strong> is verified.</p>
                        </div>
                    )}

                    {isAdmin && isDomainValid && !isSaved && (
                        <div className="mt-4">
                            <Button
                                variant={verificationFailed ? "danger" : "primary"}
                                onClick={handleVerificationAndSave}
                                disabled={isVerifying}
                                data-help-title="Verify and Save"
                                data-help="Checks that the DNS TXT record with the verification string is in place and, if verification succeeds, saves the domain as your shop domain. If it fails, DNS may not have propagated yet; wait a few minutes and try again. Admin only."
                            >
                                {isVerifying ? "Verifying..." : verificationFailed ? "Verification Failed" : "Verify and Save"}
                            </Button>
                        </div>
                    )}
                </Card>
            </div>
        </div>
    );
};

export default SettingsPage;
